import { Schemas } from "@kollectivia/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  downloadPublicCensusTemplate,
  publicPostJson,
  publicPostMultipart,
} from "../../../lib/apiPublic";
import type { PublicCensus } from "../../../lib/queries/census";

import { CHAMPS_PARTICIPANT } from "./champs";
import {
  editWorkbookField,
  seedWorkbookEntries,
  workbookDisplayValues,
  workbookEffectiveValues,
  workbookOperations,
  type InspectedWorkbook,
  type PreviewWorkbook,
  type WorkbookEntry,
} from "./clientWorkbookModel";
import { CoverageChoice } from "./CoverageChoice";
import { WrappingNativeSelect } from "./WrappingNativeSelect";

const PAGE_SIZE = 25;
const button =
  "touch-manipulation min-h-11 break-words rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50";
const input =
  "block min-h-11 w-full min-w-0 rounded-lg border border-slate-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:bg-slate-50";
const fields = CHAMPS_PARTICIPANT.map((champ) => champ.cle);
type Props = {
  token: string;
  census: PublicCensus;
  manualBlocked: boolean;
  onDirtyChange: (dirty: boolean) => void;
  onApplied: () => void;
  onEditEmployee: (rowId: string) => void;
};

export function CensusWorkbookPanel({
  token,
  census,
  manualBlocked,
  onDirtyChange,
  onApplied,
  onEditEmployee,
}: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const coverageHelpId = useId();
  const [file, setFile] = useState<File | null>(null);
  const [inspection, setInspection] = useState<InspectedWorkbook | null>(null);
  const [sheet, setSheet] = useState("");
  const [header, setHeader] = useState(1);
  const [mapping, setMapping] = useState<Record<number, string>>({});
  const [preview, setPreview] = useState<PreviewWorkbook | null>(null);
  const [imported, setImported] = useState<WorkbookEntry[]>([]);
  const [patches, setPatches] = useState<Record<string, WorkbookEntry>>({});
  const [removed, setRemoved] = useState<string[]>([]);
  const [baseRevision, setBaseRevision] = useState(census.revision);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [field, setField] = useState("birth_date");
  const [tab, setTab] = useState<"current" | "workbook">("current");
  const [detail, setDetail] = useState<string | null>(null);
  const [targetRow, setTargetRow] = useState<string | null>(null);
  const [targetSearch, setTargetSearch] = useState("");
  const targetInputRef = useRef<HTMLInputElement>(null);
  const targetOpenerRef = useRef<HTMLButtonElement | null>(null);
  const detailRef = useRef<HTMLFieldSetElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const dirty = preview !== null || Object.keys(patches).length > 0 || removed.length > 0;
  const readOnly = census.status !== "draft";
  const conflict = dirty && baseRevision !== census.revision;
  const blocked = readOnly || busy || manualBlocked;
  useEffect(() => onDirtyChange(dirty || busy), [dirty, busy, onDirtyChange]);
  useEffect(() => {
    if (!dirty) setBaseRevision(census.revision);
  }, [census.revision, dirty]);

  useEffect(() => {
    if (targetRow) targetInputRef.current?.focus();
    else if (targetOpenerRef.current?.isConnected) targetOpenerRef.current.focus();
  }, [targetRow]);
  useEffect(() => {
    if (detail) detailRef.current?.focus();
  }, [detail]);

  const currentRows = useMemo(
    () =>
      census.participants.map(
        (row) =>
          patches[row.client_row_id] ?? {
            key: row.client_row_id,
            sourceLine: 0,
            action: "update" as const,
            addId: row.client_row_id,
            targetId: row.client_row_id,
            values: {},
            editedFields: new Set<string>(),
            issues: [],
            ambiguous: false,
          },
      ),
    [census.participants, patches],
  );
  const rows = tab === "workbook" ? imported : currentRows;
  const display = (row: WorkbookEntry) => workbookDisplayValues(row, census.participants);
  const issuesFor = (row: WorkbookEntry) => {
    return Schemas.collectCensusRowIssues({
      participants: [
        { ...workbookEffectiveValues(row, census.participants), client_row_id: row.key },
      ] as never,
      dependents: [],
    });
  };
  const filtered = useMemo(
    () =>
      rows.filter((row) => {
        const values = workbookDisplayValues(row, census.participants);
        return (
          [values.employee_number, values.first_name, values.last_name]
            .join(" ")
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase()) &&
          (!errorsOnly || issuesFor(row).length > 0)
        );
      }),
    [rows, census.participants, search, errorsOnly],
  );
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const actualPage = Math.min(page, pages - 1);
  const shown = filtered.slice(actualPage * PAGE_SIZE, (actualPage + 1) * PAGE_SIZE);
  const counts = {
    added: imported.filter((row) => row.action === "add").length,
    updated: preview
      ? imported.filter((row) => row.action === "update").length
      : Object.keys(patches).filter((id) => !removed.includes(id)).length,
    removed: removed.length,
  };
  const operationCount = counts.added + counts.updated + counts.removed;
  const overLimit =
    census.participants.length + counts.added - counts.removed > census.limits.maxParticipants;
  const targetCandidates = census.participants
    .filter((person) =>
      [person.employee_number, person.first_name, person.last_name]
        .join(" ")
        .toLocaleLowerCase()
        .includes(targetSearch.toLocaleLowerCase()),
    )
    .slice(0, 15);

  function reset() {
    setPreview(null);
    setImported([]);
    setPatches({});
    setRemoved([]);
    setTab("current");
    setConfirmed(false);
    setError(null);
    setSaved(false);
    setDetail(null);
    setTargetRow(null);
  }
  function fail(code?: string) {
    const key =
      code === "CENSUS_BATCH_REVISION_CONFLICT"
        ? "conflict"
        : code === "CENSUS_WORKBOOK_LIMIT_REACHED"
          ? "quota"
          : code === "CENSUS_ROW_LIMIT_REACHED"
            ? "limit"
            : code === "CENSUS_BATCH_ROW_CONFLICT"
              ? "row_conflict"
              : code === "PUBLIC_INTAKE_LINK_UNAVAILABLE"
                ? "unavailable"
                : "failed";
    setError(t(`census.workbook.${key}`));
  }
  async function inspect(nextFile: File | null = file, selected = sheet, row = header) {
    if (!nextFile || blocked || dirty) return;
    if (
      !nextFile.name.toLowerCase().endsWith(".xlsx") ||
      nextFile.size === 0 ||
      nextFile.size > 10 * 1024 * 1024
    ) {
      setError(t("census.workbook.file_invalid"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.set("file", nextFile);
      form.set(
        "selection",
        JSON.stringify(selected ? { sheet_name: selected, header_row: row } : {}),
      );
      const result = await publicPostMultipart<InspectedWorkbook>(
        `/v1/public/census/${encodeURIComponent(token)}/workbook/inspect`,
        form,
        { timeoutMs: 30_000 },
      );
      if (!result.envelope.ok) {
        fail(result.envelope.error.code);
        return;
      }
      const value = result.envelope.data;
      setInspection(value);
      setSheet(value.selected_sheet ?? "");
      setHeader(value.header_row);
      const used = new Set<string>();
      setMapping(
        Object.fromEntries(
          value.columns.map((column) => {
            const suggestion = column.suggested_field;
            const chosen = suggestion && !used.has(suggestion) ? suggestion : "";
            if (chosen) used.add(chosen);
            return [column.index, chosen];
          }),
        ),
      );
    } catch {
      fail();
    } finally {
      setBusy(false);
    }
  }
  async function review() {
    if (!file || blocked || dirty) return;
    const selection = Schemas.CensusWorkbookSelectionSchema.safeParse({
      sheet_name: sheet,
      header_row: header,
      columns: Object.entries(mapping)
        .filter(([, value]) => value)
        .map(([index, value]) => ({ index: Number(index), field: value })),
    });
    if (!selection.success) {
      setError(t("census.workbook.mapping_invalid"));
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const form = new FormData();
      form.set("file", file);
      form.set("selection", JSON.stringify(selection.data));
      const result = await publicPostMultipart<PreviewWorkbook>(
        `/v1/public/census/${encodeURIComponent(token)}/workbook/preview`,
        form,
        { timeoutMs: 30_000 },
      );
      if (!result.envelope.ok) {
        fail(result.envelope.error.code);
        return;
      }
      if (result.envelope.data.revision !== census.revision) {
        fail("CENSUS_BATCH_REVISION_CONFLICT");
        await qc.invalidateQueries({ queryKey: ["public-census", token] });
        return;
      }
      setPreview(result.envelope.data);
      setImported(seedWorkbookEntries(result.envelope.data.rows, census.participants));
      setBaseRevision(result.envelope.data.revision);
      setTab("workbook");
      setPage(0);
      setConfirmed(false);
    } catch {
      fail();
    } finally {
      setBusy(false);
    }
  }
  function update(row: WorkbookEntry, name: string, value: string) {
    const next = editWorkbookField(row, name, value);
    if (tab === "workbook")
      setImported((old) => old.map((item) => (item.key === row.key ? next : item)));
    else setPatches((old) => ({ ...old, [row.key]: next }));
    setSaved(false);
    setConfirmed(false);
  }
  async function apply() {
    if (blocked || conflict || !confirmed || !operationCount || overLimit) return;
    const operations = preview
      ? workbookOperations(imported)
      : [
          ...workbookOperations(Object.values(patches).filter((row) => !removed.includes(row.key))),
          ...removed.map((id) => ({ operation: "remove" as const, client_row_id: id })),
        ];
    const parsed = Schemas.CensusBatchSchema.safeParse({
      revision: baseRevision,
      operations,
      ...(preview
        ? { source_file_id: preview.source_file_id, source_selection: preview.source_selection }
        : {}),
    });
    if (!parsed.success) {
      setError(t("census.workbook.review_invalid"));
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const result = await publicPostJson(
        `/v1/public/census/${encodeURIComponent(token)}/batch`,
        parsed.data,
        { timeoutMs: 30_000 },
      );
      if (!result.envelope.ok) {
        fail(result.envelope.error.code);
        if (result.envelope.error.code === "CENSUS_BATCH_REVISION_CONFLICT")
          await qc.invalidateQueries({ queryKey: ["public-census", token] });
        return;
      }
      reset();
      setSaved(true);
      onApplied();
      await qc.invalidateQueries({ queryKey: ["public-census", token] });
    } catch {
      setError(t("census.workbook.uncertain"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      className="mt-5 min-w-0 rounded-xl border border-slate-200 bg-white p-4"
      aria-label={t("census.workbook.title")}
    >
      <h2 className="text-lg font-semibold">{t("census.workbook.title")}</h2>
      <p className="mt-2 text-sm text-slate-600">{t("census.workbook.intro")}</p>
      <ol className="mt-3 grid gap-2 text-sm text-slate-700 sm:grid-cols-3">
        <li>1. {t("census.workbook.step_file")}</li>
        <li>2. {t("census.workbook.step_review")}</li>
        <li>3. {t("census.workbook.step_send")}</li>
      </ol>
      {manualBlocked && (
        <p role="status" className="mt-3 text-sm text-amber-800">
          {t("census.workbook.manual_pending")}
        </p>
      )}
      {!readOnly && (
        <fieldset disabled={busy || dirty || manualBlocked} className="mt-4 space-y-3">
          <button
            type="button"
            className={button}
            onClick={() => void downloadPublicCensusTemplate(token, "census").catch(() => fail())}
          >
            {t("census.workbook.template")}
          </button>
          <label className="block text-sm font-medium">
            {t("census.workbook.file")}
            <input
              type="file"
              accept=".xlsx"
              className={`${input} mt-1`}
              onChange={(event) => {
                const next = event.target.files?.[0] ?? null;
                setFile(next);
                setInspection(null);
                setSheet("");
                setHeader(1);
                setError(null);
                if (next) void inspect(next, "", 1);
              }}
            />
          </label>
          <p className="text-xs text-slate-500">{t("census.workbook.file_hint")}</p>
          {inspection && (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-sm">
                  {t("census.workbook.sheet")}
                  <select
                    className={`${input} mt-1`}
                    aria-label={t("census.workbook.sheet")}
                    value={sheet}
                    onChange={(event) => {
                      setSheet(event.target.value);
                      setMapping({});
                    }}
                  >
                    <option value="">{t("census.workbook.choose")}</option>
                    {inspection.sheets.map((name) => (
                      <option key={name}>{name}</option>
                    ))}
                  </select>
                </label>
                <label className="text-sm">
                  {t("census.workbook.header")}
                  <input
                    type="number"
                    min={1}
                    max={25}
                    value={header}
                    onChange={(event) => {
                      setHeader(Number(event.target.value));
                      setMapping({});
                    }}
                    className={`${input} mt-1`}
                  />
                </label>
              </div>
              <button
                type="button"
                className={button}
                disabled={!sheet}
                onClick={() => void inspect()}
              >
                {t("census.workbook.inspect")}
              </button>
              <p className="text-sm text-slate-600">{t("census.workbook.mapping_hint")}</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {inspection.columns.map((column) => (
                  <label key={column.index} className="min-w-0 text-sm">
                    <span className="break-words">{column.label || `#${column.index + 1}`}</span>
                    <select
                      className={`${input} mt-1`}
                      aria-label={column.label || `#${column.index + 1}`}
                      value={mapping[column.index] ?? ""}
                      onChange={(event) =>
                        setMapping((old) => ({ ...old, [column.index]: event.target.value }))
                      }
                    >
                      <option value="">{t("census.workbook.ignore")}</option>
                      {fields.map((name) => (
                        <option key={name} value={name}>
                          {t(`census.fields.${name}`)}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
              <button
                type="button"
                className={button}
                disabled={!sheet}
                onClick={() => void review()}
              >
                {t("census.workbook.preview")}
              </button>
            </>
          )}
        </fieldset>
      )}
      {preview && (
        <p className="mt-3 text-sm text-slate-600">
          {t("census.workbook.original_retained")} {t("census.workbook.blank_preserved")}
        </p>
      )}
      <div className="mt-5 flex flex-wrap gap-2">
        <button
          type="button"
          className={button}
          aria-pressed={tab === "current"}
          onClick={() => {
            setTab("current");
            setPage(0);
            setDetail(null);
          }}
        >
          {t("census.workbook.current", { count: census.participants.length })}
        </button>
        {preview && (
          <button
            type="button"
            className={button}
            aria-pressed={tab === "workbook"}
            onClick={() => {
              setTab("workbook");
              setPage(0);
              setDetail(null);
            }}
          >
            {t("census.workbook.imported", { count: imported.length })}
          </button>
        )}
      </div>
      <div className="my-3 grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          {t("census.workbook.search")}
          <input
            className={`${input} mt-1`}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
          />
        </label>
        <label className="text-sm">
          {t("census.workbook.field")}
          <WrappingNativeSelect
            displayValue={t(`census.fields.${field}`)}
            className="mt-1"
            aria-label={t("census.workbook.field")}
            value={field}
            onChange={(event) => setField(event.target.value)}
          >
            {fields.map((name) => (
              <option key={name} value={name}>
                {t(`census.fields.${name}`)}
              </option>
            ))}
          </WrappingNativeSelect>
        </label>
        <label className="text-sm">
          <input
            type="checkbox"
            checked={errorsOnly}
            onChange={(event) => {
              setErrorsOnly(event.target.checked);
              setPage(0);
            }}
          />{" "}
          {t("census.workbook.errors_only")}
        </label>
      </div>
      {dirty && !readOnly && (
        <div className="mt-4 space-y-3">
          <p className="font-medium">{t("census.workbook.summary", counts)}</p>
          <p className="text-sm">{t("census.workbook.no_implicit_delete")}</p>
          <label className="block text-sm">
            <input
              type="checkbox"
              checked={confirmed}
              disabled={busy || conflict}
              onChange={(event) => setConfirmed(event.target.checked)}
            />{" "}
            {t("census.workbook.confirm")}
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={`${button} bg-brand-600 text-white`}
              disabled={
                blocked || conflict || !baseRevision || !confirmed || !operationCount || overLimit
              }
              onClick={() => void apply()}
            >
              {t("census.workbook.apply")}
            </button>
            <button type="button" className={button} disabled={busy} onClick={reset}>
              {t("census.workbook.discard")}
            </button>
          </div>
        </div>
      )}
      {overLimit && (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {t("census.workbook.limit")}
        </p>
      )}
      {busy && (
        <p role="status" className="mt-3">
          {t("census.public.loading")}
        </p>
      )}
      {saved && (
        <p role="status" className="mt-3">
          {t("census.workbook.saved")}
        </p>
      )}
      {(error || conflict) && (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {error ?? t("census.workbook.conflict")}
        </p>
      )}
      {field === "coverage_choice" && (
        <p id={coverageHelpId} className="mt-3 text-sm text-slate-600">
          {t("census.coverage_help")}
        </p>
      )}
      <div className="mt-4 rounded-lg border border-slate-200">
        <table className="block w-full text-left text-sm lg:table">
          <thead className="hidden bg-slate-50 lg:table-header-group">
            <tr>
              <th scope="col" className="p-2">
                {t("census.workbook.employee")}
              </th>
              <th scope="col" className="p-2">
                {t(`census.fields.${field}`)}
              </th>
              <th scope="col" className="p-2">
                {t("census.workbook.action")}
              </th>
            </tr>
          </thead>
          <tbody className="block lg:table-row-group">
            {shown.map((row) => {
              const values = display(row),
                editable =
                  !blocked && !(tab === "current" && preview) && !removed.includes(row.key);
              return (
                <tr
                  key={row.key}
                  className="grid grid-cols-1 border-t border-slate-200 align-top lg:table-row"
                >
                  <td className="block min-w-0 p-2 lg:table-cell">
                    <strong className="block break-words">
                      {values.employee_number || t("census.public.unnamed")}
                    </strong>
                    <span className="block break-words">
                      {[values.first_name, values.last_name].filter(Boolean).join(" ")}
                    </span>
                    <span className="block text-xs text-slate-500">
                      {t("census.workbook.issues", { count: issuesFor(row).length })}
                    </span>
                  </td>
                  <td className="block min-w-0 p-2 lg:table-cell">
                    {field !== "coverage_choice" && (
                      <span className="mb-1 block text-xs font-medium text-slate-600 lg:hidden">
                        {t(`census.fields.${field}`)}
                      </span>
                    )}
                    {field === "coverage_choice" ? (
                      <CoverageChoice
                        id={`batch-${row.key}-coverage`}
                        helpId={coverageHelpId}
                        value={values[field] ?? ""}
                        readOnly={!editable}
                        error={null}
                        onChange={(value) => update(row, field, value)}
                        onBlur={() => undefined}
                      />
                    ) : (
                      <input
                        aria-label={`${t(`census.fields.${field}`)} ${values.employee_number || row.key}`}
                        className={input}
                        disabled={!editable}
                        inputMode={
                          CHAMPS_PARTICIPANT.find((champ) => champ.cle === field)?.type === "number"
                            ? "decimal"
                            : undefined
                        }
                        value={values[field] ?? ""}
                        onChange={(event) => update(row, field, event.target.value)}
                      />
                    )}
                  </td>
                  <td className="block min-w-0 p-2 lg:table-cell">
                    <div className="flex flex-col items-start gap-2">
                      <button
                        type="button"
                        className={button}
                        onClick={() => {
                          if (tab === "current" && !dirty) onEditEmployee(row.targetId!);
                          else setDetail((old) => (old === row.key ? null : row.key));
                        }}
                      >
                        {t("census.workbook.details")}
                      </button>
                      {!readOnly &&
                        (tab === "workbook" ? (
                          <>
                            <label className="text-xs">
                              {t("census.workbook.action")}
                              <select
                                className={`${input} mt-1`}
                                aria-label={`${t("census.workbook.action")} ${values.employee_number || row.key}`}
                                value={row.action}
                                disabled={blocked}
                                onChange={(event) => {
                                  setImported((old) =>
                                    old.map((item) =>
                                      item.key === row.key
                                        ? {
                                            ...item,
                                            action: event.target.value as WorkbookEntry["action"],
                                          }
                                        : item,
                                    ),
                                  );
                                  setConfirmed(false);
                                }}
                              >
                                <option value="add">{t("census.workbook.add")}</option>
                                <option value="update" disabled={!row.targetId}>
                                  {t("census.workbook.update")}
                                </option>
                                <option value="skip">{t("census.workbook.skip")}</option>
                              </select>
                            </label>
                            <button
                              type="button"
                              className={button}
                              disabled={blocked}
                              onClick={(event) => {
                                targetOpenerRef.current = event.currentTarget;
                                setTargetRow(row.key);
                                setTargetSearch(values.employee_number ?? "");
                              }}
                            >
                              {t("census.workbook.target")}
                            </button>
                            {row.targetId && (
                              <span className="text-xs text-slate-600">
                                {t("census.workbook.matched", {
                                  number:
                                    census.participants.find(
                                      (person) => person.client_row_id === row.targetId,
                                    )?.employee_number ?? "",
                                })}
                              </span>
                            )}
                            {row.ambiguous && (
                              <span className="text-xs text-amber-800">
                                {t("census.workbook.ambiguous")}
                              </span>
                            )}
                          </>
                        ) : (
                          <button
                            type="button"
                            className={button}
                            disabled={blocked || preview !== null}
                            onClick={() => {
                              setRemoved((old) =>
                                old.includes(row.key)
                                  ? old.filter((id) => id !== row.key)
                                  : [...old, row.key],
                              );
                              setConfirmed(false);
                            }}
                          >
                            {t(
                              removed.includes(row.key)
                                ? "census.workbook.undo_remove"
                                : "census.workbook.remove",
                            )}
                          </button>
                        ))}
                    </div>
                  </td>
                </tr>
              );
            })}
            {shown.length === 0 && (
              <tr>
                <td colSpan={3} className="p-4 text-slate-500">
                  {t("census.workbook.empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          className={button}
          disabled={actualPage === 0}
          onClick={() => setPage(actualPage - 1)}
        >
          {t("census.workbook.previous")}
        </button>
        <span className="text-sm">
          {t("census.workbook.page", { page: actualPage + 1, pages, count: filtered.length })}
        </span>
        <button
          type="button"
          className={button}
          disabled={actualPage >= pages - 1}
          onClick={() => setPage(actualPage + 1)}
        >
          {t("census.workbook.next")}
        </button>
      </div>
      {detail &&
        (() => {
          const row = rows.find((item) => item.key === detail);
          if (!row) return null;
          const values = display(row);
          return (
            <fieldset
              ref={detailRef}
              tabIndex={-1}
              className="focus-visible:ring-brand-500 mt-4 rounded-lg bg-slate-50 p-3 focus-visible:outline-none focus-visible:ring-2"
              disabled={blocked || (tab === "current" && preview !== null)}
            >
              <legend className="text-sm font-semibold">
                {t("census.workbook.details")}: {values.employee_number}
              </legend>
              <div className="grid gap-3 sm:grid-cols-2">
                {fields.map((name) =>
                  name === "coverage_choice" ? (
                    <CoverageChoice
                      key={name}
                      id={`detail-${row.key}-coverage`}
                      value={values[name] ?? ""}
                      readOnly={blocked}
                      error={null}
                      onChange={(value) => update(row, name, value)}
                      onBlur={() => undefined}
                    />
                  ) : (
                    <label key={name} className="min-w-0 text-sm">
                      {t(`census.fields.${name}`)}
                      <input
                        className={`${input} mt-1`}
                        value={values[name] ?? ""}
                        onChange={(event) => update(row, name, event.target.value)}
                      />
                    </label>
                  ),
                )}
              </div>
              <p className="mt-2 text-xs">{t("census.workbook.blank_preserved")}</p>
            </fieldset>
          );
        })()}
      {targetRow && (
        <div
          className="mt-4 rounded-lg border border-slate-300 p-3"
          role="region"
          aria-label={t("census.workbook.target")}
        >
          <label className="block text-sm">
            {t("census.workbook.target_search")}
            <input
              className={`${input} mt-1`}
              ref={targetInputRef}
              value={targetSearch}
              onChange={(event) => setTargetSearch(event.target.value)}
            />
          </label>
          <ul className="mt-2 space-y-1">
            {targetCandidates.map((person) => (
              <li key={person.client_row_id}>
                <button
                  type="button"
                  className={`${button} w-full text-left`}
                  disabled={blocked}
                  onClick={() => {
                    setImported((old) =>
                      old.map((row) =>
                        row.key === targetRow
                          ? {
                              ...row,
                              targetId: person.client_row_id,
                              action: "update",
                              ambiguous: false,
                            }
                          : row,
                      ),
                    );
                    setTargetRow(null);
                    setConfirmed(false);
                  }}
                >
                  {person.employee_number} — {person.first_name} {person.last_name}
                </button>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs">{t("census.workbook.target_limit")}</p>
          <button type="button" className={`${button} mt-2`} onClick={() => setTargetRow(null)}>
            {t("census.workbook.close")}
          </button>
        </div>
      )}
      <p className="mt-3 text-xs text-slate-600">{t("census.workbook.privacy")}</p>
    </section>
  );
}
